import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from "@/hooks/useToast";
import { getCurrentProvider, switchProvider } from "@/lib/ai/providers/openai";
import { MODEL_PROVIDERS, PREMIUM_MODELS } from "@/lib/constants/app";
import { getEnabledModels } from "@/lib/utils/ai";
import { cn } from "@/lib/utils";
import { useAssistantStore, useAuthStore } from "@/store";
import useModelStore from "@/store/modelStore";
import { Crown, RotateCcw } from "lucide-react";
import PropTypes from "prop-types";
import { forwardRef, useRef, useState } from "react";
import { useTouchDevice } from "@/hooks/useTouchDevice";
import { ClaudeIcon } from "../../header/icons/ClaudeIcon";
import { CohereIcon } from "../../header/icons/CohereIcon";
import { DeepSeekIcon } from "../../header/icons/DeepSeekIcon";
import { GeminiIcon } from "../../header/icons/GeminiIcon";
import { GrokIcon } from "../../header/icons/GrokIcon";
import { GroqIcon } from "../../header/icons/GroqIcon";
import { MistralIcon } from "../../header/icons/MistralIcon";
import { OpenAIIcon } from "../../header/icons/OpenAIIcon";
import { OpenRouterIcon } from "../../header/icons/OpenRouterIcon";
import { PerplexityIcon } from "../../header/icons/PerplexityIcon";

const ModelSwitchButton = forwardRef(
  ({ onClick, disabled, isProcessing, ...props }, ref) => (
    <Button
      ref={ref}
      size="icon"
      variant="outline"
      disabled={disabled}
      onClick={onClick}
      className="h-6 w-6 rounded-md bg-transparent border-0 shadow-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group focus-visible:outline-none focus-visible:bg-sidebar-accent"
      onMouseEnter={(e) => e.stopPropagation()}
      data-prevent-scroll="true"
      {...props}
    >
      <RotateCcw className="h-3 w-3 text-muted-foreground group-hover:text-sidebar-accent-foreground transition-colors" />
      <span className="sr-only">Switch model and rerun</span>
    </Button>
  )
);

ModelSwitchButton.displayName = "ModelSwitchButton";

export const ModelSwitchAndRerun = ({ message, onRerun }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const nativeSelectRef = useRef(null);

  // Detect touch devices to use native select for better scroll experience
  const { isTouchDevice } = useTouchDevice();

  const setProvider = useModelStore((state) => state.setProvider);
  const setModel = useModelStore((state) => state.setModel);
  const { isPremiumUser, user, profile } = useAuthStore((state) => state);
  const { currentAssistant, updateAssistant } = useAssistantStore();

  // Get user's model preferences from profile settings
  const userModelPreferences = profile?.settings?.models || {};

  // Filter models based on user preferences - only show enabled models
  const enabledModels = getEnabledModels(user, userModelPreferences);

  // Ensure at least one model is always available (fallback to gpt-4.1-mini)
  const finalEnabledModels =
    enabledModels.length > 0 ? enabledModels : ["gpt-4.1-mini"];

  // Get model options from enabled models only
  const modelOptions = finalEnabledModels.map((modelId) => ({
    value: modelId,
    label: modelId,
    isPremium: PREMIUM_MODELS.includes(modelId),
    isFree: true,
    provider: MODEL_PROVIDERS[modelId] || "openai",
  }));

  const getProviderIcon = (provider) => {
    switch (provider) {
      case "deepseek":
        return (
          <DeepSeekIcon
            size={14}
            className="text-[#015482] dark:text-[#015482]"
          />
        );
      case "gemini":
        return (
          <GeminiIcon
            size={14}
            className="text-[#1967D2] dark:text-[#1967D2]"
          />
        );
      case "mistral":
        return (
          <MistralIcon
            size={14}
            className="text-orange-600 dark:text-orange-400"
          />
        );
      case "anthropic":
        return <ClaudeIcon size={14} className="fill-[#D97757]" />;
      case "groq":
        return (
          <GroqIcon size={14} className="text-green-600 dark:text-green-400" />
        );
      case "xai":
        return (
          <GrokIcon size={14} className="text-blue-400 dark:text-blue-200" />
        );
      case "cohere":
        return (
          <CohereIcon size={14} className="text-teal-600 dark:text-teal-400" />
        );
      case "perplexity":
        return (
          <PerplexityIcon
            size={14}
            className="text-red-600 dark:text-red-400"
          />
        );
      case "openrouter":
        return (
          <OpenRouterIcon
            size={14}
            className="text-indigo-600 dark:text-indigo-400"
          />
        );
      case "openai":
      default:
        return (
          <OpenAIIcon
            size={14}
            className="fill-black dark:fill-white text-black dark:text-white"
          />
        );
    }
  };

  const IconWrapper = ({ className, provider = "openai" }) => {
    const borderStyle = "border border-gray-300 dark:border-gray-500";

    return (
      <div
        className={cn(
          "w-6 h-6 rounded-full flex items-center justify-center",
          "bg-transparent",
          borderStyle,
          className
        )}
      >
        {getProviderIcon(provider)}
      </div>
    );
  };

  // Determine the current provider correctly
  const determineCurrentProvider = (model) => {
    if (!model) return "openai";

    // Check for OpenRouter models which use a slash format
    if (model.includes("/")) {
      const prefix = model.split("/")[0].toLowerCase();
      if (
        prefix === "cognitivecomputations" ||
        prefix === "nousresearch" ||
        prefix === "teknium" ||
        prefix === "migtissera"
      ) {
        return "openrouter";
      }
    }

    // Check if model name includes specific provider names
    if (model.includes("deepseek")) return "deepseek";
    if (model.includes("gemini")) return "gemini";
    if (model.includes("claude")) return "anthropic";
    if (model === "mistral-large-2402") return "groq";
    if (model.includes("mistral") && !model.includes("mixtral"))
      return "mistral";
    if (model.includes("grok")) return "xai";
    if (model.includes("command")) return "cohere";
    if (model.includes("sonar") || model.includes("r1-1776"))
      return "perplexity";
    if (
      model.includes("gemma") ||
      model.includes("llama") ||
      model.includes("mixtral") ||
      (model.includes("mistral") && model.includes("mixtral"))
    )
      return "groq";

    return MODEL_PROVIDERS[model] || "openai";
  };

  const getDisplayName = (modelName) => {
    // Handle OpenRouter models with provider/model format
    if (modelName.includes("/")) {
      const parts = modelName.split("/");
      if (parts.length === 2) {
        const modelPart = parts[1];
        if (modelPart.includes("hermes")) {
          return "Hermes " + modelPart.split("-").slice(-2, -1).join(" ");
        }
        return modelPart.split("-")[0];
      }
      return modelName.split("/")[1];
    }

    // Remove various date patterns from model names
    let cleanName = modelName;

    // Handle command models first to preserve variants like -a, -r7b
    cleanName = cleanName.replace(/(command-[a-z0-9]+)-\d{2}-\d{4}$/, "$1");

    // Remove date patterns
    cleanName = cleanName.replace(/-\d{8}$/, "");
    cleanName = cleanName.replace(/-\d{4}-\d{2}-\d{2}$/, "");
    cleanName = cleanName.replace(/-\d{8}(?=-|$)/, "");
    cleanName = cleanName.replace(/-preview-\d{2}-\d{2}$/, "");
    cleanName = cleanName.replace(/-\d{4}\d{4}$/, "");
    cleanName = cleanName.replace(/-\d{2}-\d{4}$/, "");

    if (cleanName.includes("-mini-")) {
      cleanName = cleanName.split("-mini-")[0] + "-mini";
    }

    return cleanName;
  };

  const handleModelSwitchAndRerun = async (newModel) => {
    if (!user) {
      toast({
        title: "Sign in required",
        description: "Please sign in to switch models",
        variant: "destructive",
      });
      return;
    }

    if (!currentAssistant) {
      toast({
        title: "No assistant found",
        description: "Please select an assistant first",
        variant: "destructive",
      });
      return;
    }

    if (!message?.id) {
      toast({
        title: "Invalid message",
        description: "Cannot rerun: message ID is missing",
        variant: "destructive",
      });
      return;
    }

    const selectedOption = modelOptions.find((m) => m.value === newModel);

    // Check if it's a premium model and user doesn't have premium access
    if (selectedOption?.isPremium && !isPremiumUser) {
      toast({
        title: "Premium model",
        description: "This model requires a premium subscription to use.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsProcessing(true);
      setIsOpen(false);

      // Switch the provider and model
      switchProvider(newModel);
      const newProvider = getCurrentProvider();
      setProvider(newProvider);
      setModel(newModel);

      // Update the assistant with the new model and wait for completion
      await updateAssistant(currentAssistant.id, {
        ...currentAssistant,
        model: newModel,
        metadata: {
          ...currentAssistant.metadata,
          provider: newProvider,
          model_provider: newProvider,
        },
      });

      // Add a small delay to ensure the assistant update has propagated through the system
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Verify the assistant was updated before proceeding
      const { currentAssistant: updatedAssistant } =
        useAssistantStore.getState();
      if (updatedAssistant?.model !== newModel) {
        console.warn(
          `Model update verification failed. Expected: ${newModel}, Got: ${updatedAssistant?.model}`
        );
        // Force update the assistant store state
        useAssistantStore.setState((state) => ({
          ...state,
          currentAssistant: {
            ...state.currentAssistant,
            model: newModel,
            metadata: {
              ...state.currentAssistant?.metadata,
              provider: newProvider,
              model_provider: newProvider,
            },
          },
        }));
        // Add another small delay after forcing the update
        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      // Final verification before rerun
      const { currentAssistant: finalAssistant } = useAssistantStore.getState();

      // Add a small additional delay to ensure all state updates have settled
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Call the rerun function passed from parent with additional error handling
      if (onRerun) {
        try {
          await onRerun();
        } catch (rerunError) {
          console.error(
            `[ModelSwitch] Rerun failed for message ${message.id}:`,
            rerunError
          );
          // If the rerun fails due to message not found, provide more helpful feedback
          if (rerunError.message && rerunError.message.includes("not found")) {
            toast({
              title: "Message not found",
              description:
                "The message may have been deleted or modified. Please try refreshing the page.",
              variant: "destructive",
            });
          } else {
            // Re-throw other errors to be handled by the outer catch
            throw rerunError;
          }
        }
      }
    } catch (error) {
      console.error("Error switching model and rerunning:", error);

      // Provide more specific error messages based on the error type
      let errorTitle = "Failed to switch model";
      let errorDescription = error.message;

      if (error.message && error.message.includes("not found")) {
        errorTitle = "Message not found";
        errorDescription =
          "The message may have been deleted or modified. Please try refreshing the page.";
      } else if (error.message && error.message.includes("Failed to rerun")) {
        errorTitle = "Failed to rerun message";
        errorDescription =
          "The model was switched successfully, but rerunning the message failed. Please try again.";
      }

      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler for native select on touch devices
  const handleNativeSelectChange = (e) => {
    const newModel = e.target.value;
    if (newModel && newModel !== currentAssistant?.model) {
      handleModelSwitchAndRerun(newModel);
    }
    // Reset the select to allow re-selecting the same model if needed
    if (nativeSelectRef.current) {
      nativeSelectRef.current.value = "";
    }
  };

  // Touch device version - use native select triggered by button click
  if (isTouchDevice) {
    return (
      <div className="relative">
        <TooltipProvider delayDuration={400}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="outline"
                disabled={isProcessing || !user}
                onClick={() => nativeSelectRef.current?.click()}
                className="h-6 w-6 rounded-md bg-transparent border-0 shadow-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors group focus-visible:outline-none focus-visible:bg-sidebar-accent"
                onMouseEnter={(e) => e.stopPropagation()}
                data-prevent-scroll="true"
              >
                <RotateCcw className="h-3 w-3 text-muted-foreground group-hover:text-sidebar-accent-foreground transition-colors" />
                <span className="sr-only">Switch model and rerun</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent
              className="z-[10000]"
              side="bottom"
              align="center"
              sideOffset={5}
            >
              Switch model and rerun
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {/* Hidden native select - triggered by button click for better mobile UX */}
        <select
          ref={nativeSelectRef}
          onChange={handleNativeSelectChange}
          disabled={isProcessing || !user}
          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
          defaultValue=""
        >
          <option value="" disabled>
            Select model to rerun
          </option>
          {modelOptions.map((option) => {
            const isCurrentModel = currentAssistant?.model === option.value;
            const isPremiumAndCantUse = option.isPremium && !isPremiumUser;

            return (
              <option
                key={option.value}
                value={option.value}
                disabled={isCurrentModel || isPremiumAndCantUse}
              >
                {getDisplayName(option.label)}
                {isCurrentModel ? " (current)" : ""}
                {option.isPremium ? " ★" : ""}
              </option>
            );
          })}
        </select>
      </div>
    );
  }

  // Desktop version - use Radix DropdownMenu
  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <TooltipProvider delayDuration={400}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <ModelSwitchButton
                disabled={isProcessing || !user}
                isProcessing={isProcessing}
              />
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent
            className="z-[10000]"
            side="bottom"
            align="center"
            sideOffset={5}
            data-tooltip="true"
            data-prevent-scroll="true"
          >
            Switch model and rerun
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent
        side="right"
        align="start"
        className="w-56 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 shadow-lg z-[9999] focus:outline-none"
        sideOffset={5}
      >
        {/* Use native overflow instead of ScrollArea for better mobile Safari touch support */}
        <div className="max-h-[300px] overflow-y-auto pr-2 scrollbar-thin">
          {modelOptions.map((option) => {
            const provider = determineCurrentProvider(option.value);
            const isCurrentModel = currentAssistant?.model === option.value;

            return (
              <DropdownMenuItem
                key={option.value}
                onClick={() => handleModelSwitchAndRerun(option.value)}
                disabled={
                  isProcessing ||
                  isCurrentModel ||
                  (option.isPremium && !isPremiumUser)
                }
                className={cn(
                  "flex items-center justify-between cursor-pointer hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors px-2 py-1.5",
                  "focus:outline-none focus:bg-accent",
                  isCurrentModel && "bg-blue-50 dark:bg-blue-900/20",
                  option.isPremium &&
                    !isPremiumUser &&
                    "opacity-50 cursor-not-allowed"
                )}
              >
                <div className="flex items-center gap-2">
                  <IconWrapper provider={provider} />
                  <span
                    className={cn(
                      "text-sm font-medium",
                      isCurrentModel
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-gray-700 dark:text-zinc-300"
                    )}
                  >
                    {getDisplayName(option.label)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {isCurrentModel && (
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      Current
                    </span>
                  )}
                  {option.isPremium && (
                    <Crown className="h-4 w-4 text-yellow-500" />
                  )}
                </div>
              </DropdownMenuItem>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

ModelSwitchAndRerun.propTypes = {
  message: PropTypes.object.isRequired,
  onRerun: PropTypes.func.isRequired,
};

export default ModelSwitchAndRerun;
